import { useState, useEffect, useMemo } from 'react';
import {
  getFlaggedWords,
  addFlaggedWord,
  removeFlaggedWord,
  updateFlaggedWords,
} from '../../services/firebase';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import toast from 'react-hot-toast';

interface WordEntry {
  word: string;
  replacement: string;
}

export function FlaggedWordsManager() {
  const [words, setWords] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Add/Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWord, setEditingWord] = useState<string | null>(null);
  const [formWord, setFormWord] = useState('');
  const [formReplacement, setFormReplacement] = useState('');

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadWords();
  }, []);

  const loadWords = async () => {
    try {
      const data = await getFlaggedWords();
      setWords(data?.words || {});
    } catch (error) {
      console.error('Error loading flagged words:', error);
      toast.error('Failed to load flagged words');
    } finally {
      setLoading(false);
    }
  };

  const wordEntries = useMemo<WordEntry[]>(() => {
    return Object.entries(words)
      .map(([word, replacement]) => ({ word, replacement }))
      .sort((a, b) => a.word.localeCompare(b.word));
  }, [words]);

  const filteredEntries = useMemo(() => {
    if (!searchTerm) return wordEntries;
    const term = searchTerm.toLowerCase();
    return wordEntries.filter(
      (entry) =>
        entry.word.toLowerCase().includes(term) ||
        entry.replacement.toLowerCase().includes(term)
    );
  }, [wordEntries, searchTerm]);

  const openAddModal = () => {
    setEditingWord(null);
    setFormWord('');
    setFormReplacement('');
    setIsModalOpen(true);
  };

  const openEditModal = (entry: WordEntry) => {
    setEditingWord(entry.word);
    setFormWord(entry.word);
    setFormReplacement(entry.replacement);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingWord(null);
    setFormWord('');
    setFormReplacement('');
  };

  const handleSave = async () => {
    const wordTrimmed = formWord.trim().toLowerCase();
    const replacementTrimmed = formReplacement.trim();

    if (!wordTrimmed) {
      toast.error('Word is required');
      return;
    }

    if (!replacementTrimmed) {
      toast.error('Replacement is required');
      return;
    }

    // Check for duplicate if adding new or changing the word
    if (!editingWord && words[wordTrimmed]) {
      toast.error('This word already exists');
      return;
    }

    if (editingWord && editingWord !== wordTrimmed && words[wordTrimmed]) {
      toast.error('This word already exists');
      return;
    }

    setIsProcessing(true);
    try {
      if (editingWord && editingWord !== wordTrimmed) {
        // Word changed - remove old and add new
        const newWords = { ...words };
        delete newWords[editingWord];
        newWords[wordTrimmed] = replacementTrimmed;
        await updateFlaggedWords(newWords);
        setWords(newWords);
      } else {
        // Add or update
        await addFlaggedWord(wordTrimmed, replacementTrimmed);
        setWords((prev) => ({ ...prev, [wordTrimmed]: replacementTrimmed }));
      }

      toast.success(editingWord ? 'Word updated successfully' : 'Word added successfully');
      closeModal();
    } catch (error) {
      console.error('Error saving word:', error);
      toast.error('Failed to save word');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (word: string) => {
    setIsProcessing(true);
    try {
      await removeFlaggedWord(word);
      setWords((prev) => {
        const newWords = { ...prev };
        delete newWords[word];
        return newWords;
      });
      toast.success('Word deleted successfully');
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting word:', error);
      toast.error('Failed to delete word');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Flagged Words</h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage words that should be replaced in ad content to avoid platform restrictions.
        </p>
      </div>

      {/* Search and Add */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
        <div className="relative w-full sm:flex-1">
          <input
            type="text"
            placeholder="Search words or replacements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <svg
            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <Button onClick={openAddModal}>+ Add Word</Button>
      </div>

      {/* Words Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Flagged Word
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Replacement
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredEntries.map((entry) => (
                <tr key={entry.word} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">
                      {entry.word}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{entry.replacement}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(entry)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(entry.word)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEntries.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {searchTerm
              ? 'No words match your search'
              : 'No flagged words yet. Click "Add Word" to create one.'}
          </div>
        )}
      </div>

      <p className="text-sm text-gray-500">
        {filteredEntries.length} of {wordEntries.length} words
      </p>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingWord ? 'Edit Flagged Word' : 'Add Flagged Word'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Flagged Word
            </label>
            <input
              type="text"
              value={formWord}
              onChange={(e) => setFormWord(e.target.value)}
              placeholder="e.g., rolex"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              The word that should be detected and replaced (case-insensitive)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Replacement
            </label>
            <input
              type="text"
              value={formReplacement}
              onChange={(e) => setFormReplacement(e.target.value)}
              placeholder="e.g., RLX"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              The text to use instead of the flagged word
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={closeModal} disabled={isProcessing}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={isProcessing}
              disabled={!formWord.trim() || !formReplacement.trim()}
            >
              {editingWord ? 'Save' : 'Add'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Flagged Word"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete the word "{deleteConfirm}"? This action
            cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirm(null)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              loading={isProcessing}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
